import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Loader2, ExternalLink } from 'lucide-react';
import { apiService } from '@/lib/api-functions/api-service';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface AffectedItem {
  id: string;
  name: string;
  status?: string;
  folder?: string | null;
  blueprintVersion?: { major: number; minor: number; patch: number };
  affectedFields: Array<{
    type: string;
    fieldId: string;
    fieldLabel: string;
    sectionId?: string;
    sectionTitle?: string;
    currentValue?: any;
    hasValue?: boolean;
    isRequired?: boolean;
    reason?: string;
  }>;
  requiresManualReview: boolean;
}

interface BreakingChangesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  blueprintId: string;
  version: { major: number; minor: number; patch: number };
  severityDetails?: any;
}

export function BreakingChangesDialog({
  isOpen,
  onClose,
  blueprintId,
  version,
  severityDetails
}: BreakingChangesDialogProps) {
  const { t } = usePlatformTranslation();
  const [loading, setLoading] = useState(true);
  const [affectedItems, setAffectedItems] = useState<AffectedItem[]>([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Load affected items when dialog opens
  useEffect(() => {
    if (isOpen && blueprintId && version) {
      loadAffectedItems();
    }
  }, [isOpen, blueprintId, version]);

  const loadAffectedItems = async () => {
    setLoading(true);
    try {
      const result = await apiService.fetchBlueprintAffectedItems(
        blueprintId,
        version.major,
        version.minor,
        version.patch
      );

      if (result.success) {
        // Only show items with breaking changes
        const breakingItems = result.data?.affectedItems?.breaking || [];
        setAffectedItems(breakingItems);
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to load affected items',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error loading affected items:', error);
      toast({
        title: 'Error',
        description: 'Failed to load affected items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenItem = (itemId: string) => {
    // Navigate to the library object editor
    navigate(`/library/${itemId}`);
    onClose();
  };

  const renderBreakingChangesSummary = () => {
    if (!severityDetails || !severityDetails.breakingChanges) {
      return null;
    }

    const breakingChanges = severityDetails.breakingChanges;

    return (
      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="font-semibold text-red-900 mb-2">{t('platform.blueprint-version.breakingchangesdialog.breaking_changes_detected')}</h4>
            <p className="text-sm text-red-800 mb-3">
              The following changes require manual review of each affected item:
            </p>
            <ul className="list-disc list-inside text-sm text-red-800 space-y-1">
              {breakingChanges.map((change: any, idx: number) => (
                <li key={idx}>
                  <span className="font-medium">{change.fieldLabel || change.fieldId}:</span> {change.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      );
    }

    if (affectedItems.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mb-3" />
          <p className="text-gray-600">{t('platform.blueprint-version.breakingchangesdialog.no_items_with_breaking_changes_found')}</p>
        </div>
      );
    }

    return (
      <>
        {renderBreakingChangesSummary()}

        <div className="mb-3">
          <p className="text-sm font-medium text-gray-700">
            {affectedItems.length} item{affectedItems.length !== 1 ? 's' : ''} require{affectedItems.length === 1 ? 's' : ''} manual review:
          </p>
        </div>

        <div className="h-[400px] overflow-y-auto pr-4">
          <div className="space-y-3">
            {affectedItems.map((item) => (
              <div
                key={item.id}
                className="p-4 border border-red-200 rounded-md bg-white hover:bg-red-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm mb-1">{item.name}</h4>
                    {item.status && (
                      <Badge variant="outline" className="text-xs">
                        {item.status}
                      </Badge>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenItem(item.id)}
                    className="flex items-center gap-1"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Edit
                  </Button>
                </div>

                {item.affectedFields.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs font-medium text-gray-700">{t('platform.blueprint-version.breakingchangesdialog.issues')}</div>
                    {item.affectedFields.map((field, idx) => (
                      <div key={idx} className="text-xs bg-gray-50 p-2 rounded border border-gray-200">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{field.fieldLabel}</div>
                            {field.sectionTitle && (
                              <div className="text-gray-500 text-xs">in {field.sectionTitle}</div>
                            )}
                            <div className="text-gray-600 mt-1">
                              {field.type === 'missing_field' && field.isRequired && (
                                <span className="text-red-600">{t('platform.blueprint-version.breakingchangesdialog.required_field_is_missing')}</span>
                              )}
                              {field.type === 'obsolete_field' && field.hasValue && (
                                <span className="text-orange-600">Field removed but has value: {JSON.stringify(field.currentValue)}</span>
                              )}
                              {field.type === 'modified_field' && field.hasValue && (
                                <span className="text-yellow-600">{t('platform.blueprint-version.breakingchangesdialog.field_changed_and_has_value')}</span>
                              )}
                            </div>
                            {field.reason && (
                              <div className="text-gray-500 text-xs mt-0.5 italic">{field.reason}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-xs text-yellow-800">
            <strong>{t('platform.blueprint-version.breakingchangesdialog.note')}</strong> Each item must be reviewed and updated manually to ensure data integrity. 
            Click "Edit" next to each item to make the necessary changes.
          </p>
        </div>
      </>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-center justify-center">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Breaking Changes - Manual Review Required
          </DialogTitle>
          <DialogDescription className="text-center">
            Template version {version.major}.{version.minor}.{version.patch} contains breaking changes
          </DialogDescription>
        </DialogHeader>

        {renderContent()}

        <DialogFooter>
          <Button onClick={onClose}>{t('platform.common.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BreakingChangesDialog;

